import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Mail, MessageCircle, Phone, PinIcon, Send } from 'lucide-react';
import ScrollFadeHeading from './ScrollFadeHeading';

const ContactSection: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactMethods = [
    {
      icon: <PinIcon />,
      label: t('contact.methods.address', 'Adresse'),
      value: 'Antananarivo, Madagascar',
      href: undefined,
    },
    {
      icon: <Mail />,
      label: t('contact.methods.email', 'Email'),
      value: 'eudora.direction@gmail.com',
      href: 'mailto:eudora.direction@gmail.com',
    },
    {
      icon: <Phone />,
      label: t('contact.methods.phone', 'Téléphone'),
      value: ' +261 38 13 563 41',
      href: 'tel: +261381356341',
    },
    {
      icon: <MessageCircle />,
      label: t('contact.methods.whatsapp', 'Whatsapp'),
      value: ' +261 34 00 136 16',
      href: 'tel: +261340013616',
    },
  ];

  const hours = [
    { day: t('contact.hours.weekdays', 'Lundi - Vendredi'), time: '08:00 - 17:00' },
    { day: t('contact.hours.saturday', 'Samedi'), time: '09:00 - 12:00' },
    { day: t('contact.hours.sunday', 'Dimanche'), time: t('contact.hours.closed', 'Fermé') },
  ];

  const handleChange = (field: 'name' | 'email' | 'message') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const subject = `Message de ${formData.name} — site Eudora Aromas`;
    const body = `${formData.message}\n\n—\n${formData.name}\n${formData.email}`;
    window.location.href = `mailto:eudora.direction@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    toast.success(t('contact.form.success', 'Votre client mail va s\'ouvrir pour envoyer le message.'));
    setFormData({ name: '', email: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24">
      <div className="max-w-6xl mx-auto px-4">
        <ScrollFadeHeading className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-coffee-200 mb-4">
            {t('contact.title', 'Contactez-nous')}
          </h2>
          <div className="w-24 h-1 bg-forest-200 mx-auto rounded-full"></div>
          <p className="mt-6 text-coffee-100 max-w-2xl mx-auto">
            {t('contact.subtitle', 'Producteur et exportateur direct de Madagascar. Nous sommes à votre écoute pour toute demande de partenariat ou d\'information.')}
          </p>
        </ScrollFadeHeading>

        <div className="grid gap-6 lg:grid-cols-2 items-stretch">
          {/* Formulaire de contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/55 backdrop-blur-xl rounded-3xl p-8 shadow-card border border-white/60"
          >
            <h3 className="font-display text-xl font-bold text-coffee-200 mb-1">
              {t('contact.form.title', 'Envoyez-nous un message')}
            </h3>
            <p className="text-sm text-coffee-100 mb-6">
              {t('contact.form.subtitle', 'Une question, un projet ? Écrivez-nous directement.')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">
                  {t('contact.form.name', 'Nom complet')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder={t('contact.form.namePlaceholder', 'Votre nom')}
                  className="w-full rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">
                  {t('contact.form.email', 'Email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange('email')}
                  placeholder={t('contact.form.emailPlaceholder', 'vous@exemple.com')}
                  className="w-full rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">
                  {t('contact.form.message', 'Votre message')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange('message')}
                  placeholder={t('contact.form.messagePlaceholder', 'Dites-nous en quoi nous pouvons vous aider…')}
                  className="w-full resize-none rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center gap-2 rounded-full bg-forest-200 px-6 py-2.5 text-sm font-semibold text-cream-50 shadow-soft transition-all hover:bg-forest-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {t('contact.form.submit', 'Envoyer le message')}
              </button>
            </form>
          </motion.div>

          {/* Coordonnées & horaires */}
          <div className="flex h-full flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/55 backdrop-blur-xl rounded-3xl p-8 shadow-card border border-white/60"
            >
              <h3 className="font-display text-xl font-bold text-coffee-200 mb-6">
                {t('contact.info.title', 'Nos coordonnées')}
              </h3>
              <div className="space-y-5">
                {contactMethods.map((method) => (
                  <div key={method.label} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-forest-200/10 text-forest-200 flex items-center justify-center flex-shrink-0">
                      {method.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-coffee-100 mb-0.5">{method.label}</div>
                      {method.href ? (
                        <a href={method.href} className="text-coffee-200 font-semibold break-words hover:text-forest-200 transition-colors">
                          {method.value}
                        </a>
                      ) : (
                        <div className="text-coffee-200 font-semibold break-words">{method.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Horaires */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-coffee-200/85 backdrop-blur-xl rounded-3xl p-8 text-cream-50 relative overflow-hidden shadow-card flex-1"
            >
              <div className="relative z-10">
                <h3 className="font-display text-xl font-bold mb-1">
                  {t('contact.hours_title', 'Horaires d\'ouverture')}
                </h3>
                <p className="text-cream-100/70 text-sm italic mb-5">{t('contact.hoursTimezone', 'Heure de Madagascar (GMT +3)')}</p>
                <div className="space-y-3">
                  {hours.map((h) => (
                    <div key={h.day} className="flex items-center justify-between gap-4 border-t border-cream-50/10 pt-3 first:border-t-0 first:pt-0">
                      <div className="text-cream-100/60 text-xs uppercase tracking-wide">{h.day}</div>
                      <div className="font-semibold text-sm">{h.time}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-forest-200/20 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
